import RemoveIcon from '@mui/icons-material/Remove';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CustomPhoneInput from '@/components/CustomPhoneInput';

export interface IDVClientRowData {
    firstName: string;
    lastName: string;
    contactEmail: string;
    phoneNumber: string;
    countryCode?: string;
    isSecondaryIdRequired?: boolean;
    isBankingVerificationRequired?: boolean;
}

export interface IDVClientRowErrors {
    firstName?: string;
    lastName?: string;
    contactEmail?: string;
    phoneNumber?: string;
}

type ClientField = keyof Pick<
    IDVClientRowData,
    | 'firstName'
    | 'lastName'
    | 'contactEmail'
    | 'phoneNumber'
    | 'isSecondaryIdRequired'
    | 'isBankingVerificationRequired'
>;

interface Props {
    client: IDVClientRowData;
    errors: IDVClientRowErrors;
    title?: string;
    required?: boolean;
    onRemove?: () => void;
    onChange: (field: ClientField, value: string | boolean) => void;
    onCountryChange: (country: { code: string; dialCode: string }) => void;
    onResetErrors?: () => void;
}

export default function IDVClientRow({
    client,
    errors,
    title,
    required = false,
    onRemove,
    onChange,
    onCountryChange,
    onResetErrors,
}: Props) {
    const handleFieldChange = (field: ClientField, value: string | boolean) => {
        onChange(field, value);
        onResetErrors?.();
    };

    return (
        <Stack spacing={1.5}>
            {title && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={600} color="#202124">
                        {title}
                    </Typography>
                    {onRemove && (
                        <Button
                            type="button"
                            color="error"
                            size="small"
                            startIcon={<RemoveIcon fontSize="small" />}
                            onClick={onRemove}
                            sx={{ textTransform: 'none' }}
                        >
                            Remove
                        </Button>
                    )}
                </Stack>
            )}

            <Stack direction="row" gap={1.5}>
                <TextField
                    fullWidth
                    required={required}
                    type="text"
                    label="First Name"
                    placeholder="Enter first name"
                    value={client.firstName}
                    name="clientFirstName"
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    sx={{ backgroundColor: 'white' }}
                    onChange={(event) => handleFieldChange('firstName', event.target.value)}
                />
                <TextField
                    fullWidth
                    required={required}
                    type="text"
                    label="Last Name"
                    placeholder="Enter last name"
                    value={client.lastName}
                    name="clientLastName"
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    sx={{ backgroundColor: 'white' }}
                    onChange={(event) => handleFieldChange('lastName', event.target.value)}
                />
                <TextField
                    fullWidth
                    required={required}
                    type="email"
                    label="Email"
                    placeholder="Enter email address"
                    value={client.contactEmail}
                    name="clientEmail"
                    error={!!errors.contactEmail}
                    helperText={errors.contactEmail}
                    sx={{ backgroundColor: 'white' }}
                    onChange={(event) => handleFieldChange('contactEmail', event.target.value)}
                />
            </Stack>

            <Stack direction="row" gap={1.5} alignItems="center">
                <Box sx={{ width: 320, flexShrink: 0 }}>
                    <CustomPhoneInput
                        compUsage="IDV"
                        value={client.phoneNumber}
                        selectedCountryCode={client.countryCode || 'ca'}
                        onChange={(value) => handleFieldChange('phoneNumber', value)}
                        onCountryChange={onCountryChange}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber || ''}
                    />
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={client.isSecondaryIdRequired || false}
                            onChange={(event) =>
                                onChange('isSecondaryIdRequired', event.target.checked)
                            }
                        />
                    }
                    label="Request Second ID"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={client.isBankingVerificationRequired || false}
                            onChange={(event) =>
                                onChange('isBankingVerificationRequired', event.target.checked)
                            }
                        />
                    }
                    label="Include Banking Verification"
                />
            </Stack>
        </Stack>
    );
}
